import React, { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import PrivacyPage from './PrivacyPage';

// ── COLOUR TOKENS ──────────────────────────────────────
const C = {
  orange: '#FF6B35', orangeL: '#FFF0EB', orangeD: '#E8521C',
  teal: '#2EC4B6', tealL: '#E0F7F5',
  yellow: '#FFD166', yellowL: '#FFF8E6',
  green: '#06D6A0', purple: '#6C63FF',
  dark: '#1A1A2E', text: '#2D3436', muted: '#636E72',
  bg: '#FEFBF4', white: '#fff',
  border: 'rgba(26,26,46,.07)',
};

const APP_URL = 'https://app.superfitfamily.in';

// ── FADE-UP HOOK ───────────────────────────────────────
const useFadeUp = () => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('in');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.07 });
    el.querySelectorAll('.fade-up').forEach(child => io.observe(child));
    return () => io.disconnect();
  }, []);
  return ref;
};

// ── LANDING PAGE ───────────────────────────────────────
const LandingPage = () => {
  const containerRef = useFadeUp();

  useEffect(() => {
    // Nav scroll behavior
    const navOpen = document.getElementById('nav-open');
    if (navOpen && window.innerWidth >= 768) {
      const handler = () => {
        (navOpen as HTMLElement).style.display = window.scrollY > 180 ? 'inline-flex' : 'none';
      };
      window.addEventListener('scroll', handler, { passive: true });
      return () => window.removeEventListener('scroll', handler);
    }
  }, []);

  return (
    <div ref={containerRef} style={{ fontFamily: "'Nunito', sans-serif", background: C.bg, color: C.text, fontSize: 16, lineHeight: 1.7, overflowX: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fredoka+One&family=Nunito:wght@400;600;700;800&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        h1, h2, h3, h4 { font-family: 'Fredoka One', cursive; line-height: 1.2; color: ${C.dark}; }
        a { text-decoration: none; color: inherit; }
        svg { display: block; }
        .btn {
          display: inline-flex; align-items: center; justify-content: center; gap: 8px;
          padding: 11px 22px; border-radius: 100px;
          font-family: 'Nunito', sans-serif; font-weight: 700; font-size: .92rem;
          border: none; cursor: pointer;
          transition: transform .17s, box-shadow .17s, background .17s;
          white-space: nowrap;
        }
        .btn:hover { transform: translateY(-1px); }
        .btn:active { transform: translateY(0); }
        .btn-primary { background: ${C.orange}; color: #fff; box-shadow: 0 4px 16px rgba(255,107,53,.3); }
        .btn-primary:hover { background: ${C.orangeD}; box-shadow: 0 6px 20px rgba(255,107,53,.42); }
        .btn-ghost { background: transparent; color: ${C.dark}; border: 1.5px solid rgba(26,26,46,.13); }
        .btn-ghost:hover { background: #fff; border-color: rgba(26,26,46,.22); }
        .btn-lg { padding: 14px 30px; font-size: 1.02rem; }
        .fade-up { opacity: 0; transform: translateY(24px); transition: opacity .6s ease, transform .6s ease; }
        .fade-up.in { opacity: 1; transform: translateY(0); }
        @media (max-width: 767px) { #nav-open { display: none !important; } }
      `}</style>

      {/* NAV */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'rgba(254,251,244,.93)',
        backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
        borderBottom: `1px solid ${C.border}`,
      }}>
        <div style={{
          maxWidth: 1100, margin: '0 auto',
          padding: '0 1.25rem', height: 64,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <a href="/" style={{ display: 'flex', alignItems: 'center', gap: 9, fontFamily: "'Fredoka One', cursive", fontSize: '1.18rem', color: C.orange }}>
            <svg width="30" height="30" viewBox="0 0 32 32" fill="none">
              <circle cx="16" cy="16" r="16" fill="#FF6B35"/>
              <path d="M16 6.5L18.8 13.2 26.2 14.2 21 19.3 22.6 26.6 16 23 9.4 26.6 11 19.3 5.8 14.2 13.2 13.2Z" fill="#FFD166"/>
            </svg>
            SuperFit Family
          </a>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <a id="nav-open" href={APP_URL} className="btn btn-ghost" style={{ display: 'none' }}>Open app</a>
            <a href="#download" className="btn btn-primary">Download free</a>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <div style={{ overflow: 'hidden' }}>
        <div style={{
          maxWidth: 1100, margin: '0 auto',
          padding: '40px 1.25rem 48px',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 32,
        }} className="hero-flex">
          <style>{`
            @media (min-width: 768px) {
              .hero-flex { flex-direction: row !important; align-items: center !important; padding: 72px 2rem 64px !important; gap: 56px !important; }
              .hero-visual { order: 2 !important; max-width: 380px !important; flex-shrink: 0; }
              .hero-content { order: 1 !important; }
            }
          `}</style>
          <div className="hero-visual fade-up" style={{ order: 1, width: '100%', maxWidth: 300, transitionDelay: '.1s' }}>
            <svg viewBox="0 0 340 360" fill="none" style={{ width: '100%', height: 'auto' }}>
              <circle cx="170" cy="188" r="155" fill="#FFF0EB"/>
              <circle cx="58" cy="70" r="38" fill="#FFD166" opacity=".28"/>
              <circle cx="300" cy="292" r="44" fill="#2EC4B6" opacity=".16"/>
              <g transform="translate(272,63)"><circle r="26" fill="#FFD166" opacity=".95"/><path d="M0-11C-7-11-12-5-12 4-12 13-6 18 0 18 6 18 12 13 12 4 12-5 7-11 0-11Z" fill="#FF6B35"/><path d="M0-11L2-17C3-19 5-20 6-18" stroke="#4a7c3f" strokeWidth="2" strokeLinecap="round" fill="none"/></g>
              <g transform="translate(52,106)"><circle r="24" fill="#E0F7F5" opacity=".95"/><rect x="-9" y="-9" width="18" height="18" rx="2" fill="#2EC4B6"/><path d="M-6-3L6-3M-6 1L6 1M-6 5L4 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" fill="none"/></g>
              <g transform="translate(300,156)"><circle r="22" fill="#FFF0EB" opacity=".95"/><path d="M0-13L3-4 13-4 5.5 2 8 11 0 6-8 11-5.5 2-13-4-3-4Z" fill="#FF6B35"/></g>
              <g transform="translate(48,247)"><circle r="20" fill="#EEEDFE" opacity=".95"/><path d="M-3-9C-3 2 4 9 13 7 6 13-9 11-11 0-13-9-3-15-3-9Z" fill="#6C63FF"/></g>
              <path d="M118 50L120 44 122 50 128 52 122 54 120 60 118 54 112 52Z" fill="#FFD166" opacity=".6"/>
              <path d="M242 300L243.5 296 245 300 249 301 245 302 243.5 306 242 302 238 301Z" fill="#FF6B35" opacity=".5"/>
              <path d="M89 184L90 181 91 184 94 185 91 186 90 189 89 186 86 185Z" fill="#2EC4B6" opacity=".5"/>
              <ellipse cx="170" cy="332" rx="52" ry="10" fill="rgba(255,107,53,.09)"/>
              <rect x="151" y="276" width="20" height="55" rx="10" fill="#2D3436"/>
              <rect x="175" y="276" width="20" height="55" rx="10" fill="#2D3436"/>
              <ellipse cx="161" cy="332" rx="18" ry="8" fill="#1A1A2E"/>
              <ellipse cx="185" cy="332" rx="18" ry="8" fill="#1A1A2E"/>
              <rect x="144" y="211" width="62" height="70" rx="14" fill="#FF6B35"/>
              <path d="M170 226L173 234 182 234 175.5 239 178 248 170 243 162 248 164.5 239 158 234 167 234Z" fill="#FFD166"/>
              <path d="M146 229C130 212 116 192 110 167" stroke="#D4956A" strokeWidth="20" strokeLinecap="round" fill="none"/>
              <circle cx="109" cy="162" r="13" fill="#D4956A"/>
              <path d="M204 229C220 212 234 192 240 167" stroke="#D4956A" strokeWidth="20" strokeLinecap="round" fill="none"/>
              <circle cx="241" cy="162" r="13" fill="#D4956A"/>
              <path d="M146 221C128 257 136 297 147 313L154 281Z" fill="#2EC4B6" opacity=".82"/>
              <path d="M204 221C222 257 214 297 203 313L196 281Z" fill="#2EC4B6" opacity=".82"/>
              <circle cx="170" cy="191" r="45" fill="#D4956A"/>
              <path d="M127 189C127 156 144 135 170 135 196 135 213 156 213 189Z" fill="#1A1A2E"/>
              <path d="M125 187C123 173 126 159 133 151" stroke="#1A1A2E" strokeWidth="9" strokeLinecap="round" fill="none"/>
              <circle cx="157" cy="188" r="7.5" fill="white"/><circle cx="183" cy="188" r="7.5" fill="white"/>
              <circle cx="159" cy="190" r="4.5" fill="#1A1A2E"/><circle cx="185" cy="190" r="4.5" fill="#1A1A2E"/>
              <circle cx="161" cy="188" r="1.5" fill="white"/><circle cx="187" cy="188" r="1.5" fill="white"/>
              <path d="M157 205C163 213 177 213 183 205" stroke="#B87348" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
              <ellipse cx="149" cy="201" rx="7" ry="4.5" fill="rgba(255,107,53,.27)"/>
              <ellipse cx="191" cy="201" rx="7" ry="4.5" fill="rgba(255,107,53,.27)"/>
              <circle cx="255" cy="149" r="21" fill="white" opacity=".88"/>
              <path d="M244 149L250 156 266 140" stroke="#06D6A0" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              <rect x="74" y="139" width="58" height="27" rx="8" fill="white" opacity=".9"/>
              <text x="103" y="157" fontFamily="Nunito,sans-serif" fontWeight="800" fontSize="12.5" fill="#FF6B35" textAnchor="middle">+50 pts!</text>
            </svg>
          </div>
          <div className="hero-content fade-up" style={{ order: 2, width: '100%', maxWidth: 520 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: C.orangeL, color: C.orangeD,
              fontSize: '.72rem', fontWeight: 800, padding: '6px 14px',
              borderRadius: 100, marginBottom: 18,
              letterSpacing: '.5px', textTransform: 'uppercase',
            }}>⭐ Built for Indian families</div>
            <h1 style={{ fontSize: 'clamp(2rem, 8vw, 3.4rem)', marginBottom: 16, fontFamily: "'Fredoka One', cursive" }}>
              From <span style={{ color: C.orange }}>"I told you to"</span>
              <span style={{ color: C.teal, fontSize: '.72em', margin: '0 5px' }}>→</span>
              <span style={{ color: C.orange }}>"I want to"</span>
            </h1>
            <p style={{ fontSize: '1.03rem', color: C.muted, marginBottom: 26, lineHeight: 1.8 }}>
              SuperFit Family turns daily habits into something your kids actually look forward to — with streaks, rewards, and just enough excitement to make brushing teeth feel like winning.
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <a href="#download" className="btn btn-primary btn-lg">Get the app free</a>
              <a href="#how" className="btn btn-ghost btn-lg">How it works</a>
            </div>
          </div>
        </div>
      </div>

      {/* STATS */}
      <div style={{ background: C.dark, padding: '28px 1.25rem' }}>
        <style>{`
          .stats-inner { display: grid; grid-template-columns: 1fr 1fr; gap: 20px 12px; text-align: center; max-width: 1100px; margin: 0 auto; }
          @media (min-width: 600px) { .stats-inner { display: flex; justify-content: center; gap: 0; } .stats-inner > div { flex: 1; max-width: 180px; } }
        `}</style>
        <div className="stats-inner">
          {[
            { num: '2–14', label: 'Age range' },
            { num: '25+', label: 'Habit types' },
            { num: '6', label: 'Languages coming' },
            { num: '₹0', label: 'To get started' },
          ].map(s => (
            <div key={s.label}>
              <span style={{ fontFamily: "'Fredoka One', cursive", fontSize: '2rem', color: C.yellow, display: 'block', lineHeight: 1 }}>{s.num}</span>
              <span style={{ fontSize: '.75rem', color: 'rgba(255,255,255,.42)', marginTop: 3, display: 'block' }}>{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* HONEST TRUTH */}
      <section style={{ padding: '60px 1.25rem' }}>
        <style>{`@media (min-width: 768px) { section { padding: 80px 2rem !important; } }`}</style>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div className="fade-up">
            <span style={{ display: 'block', fontSize: '.71rem', fontWeight: 800, letterSpacing: '2.5px', textTransform: 'uppercase', color: C.orange, marginBottom: 10 }}>The honest truth</span>
            <h2 style={{ fontSize: 'clamp(1.7rem, 4vw, 2.55rem)', marginBottom: 13, fontFamily: "'Fredoka One', cursive" }}>Sound familiar?</h2>
            <p style={{ fontSize: '1rem', color: C.muted, maxWidth: 520, lineHeight: 1.8 }}>Most habit apps are built for adults. Kids need something different — and so do you.</p>
          </div>
          <style>{`
            .honest-grid { display: grid; grid-template-columns: 1fr; gap: 12px; margin-top: 38px; }
            @media (min-width: 540px) { .honest-grid { grid-template-columns: 1fr 1fr; gap: 13px; } }
          `}</style>
          <div className="honest-grid">
            {[
              { icon: '😮‍💨', text: 'You\'ve said "brush your teeth" at least 400 times this month. Maybe 500.', hl: false },
              { icon: '📱', text: 'Screen time is the only reward that works — and you feel guilty every single time.', hl: true },
              { icon: '😤', text: 'Every morning, you end up being the bad guy. Before 8 AM. Again.', hl: false },
              { icon: '💛', text: 'You know they\'re capable. They just need a reason that feels like theirs — not yours.', hl: false },
            ].map((c, i) => (
              <div key={i} className="fade-up" style={{
                background: c.hl ? C.orangeL : '#fff',
                border: `1px solid ${c.hl ? 'rgba(255,107,53,.1)' : C.border}`,
                borderRadius: 20, padding: '22px 20px',
                transition: 'transform .28s, box-shadow .28s',
                transitionDelay: `${0.05 * (i + 1)}s`,
              }}>
                <span style={{ fontSize: '1.65rem', display: 'block', marginBottom: 10 }}>{c.icon}</span>
                <p style={{ fontWeight: 600, lineHeight: 1.55, fontSize: '.96rem', margin: 0 }}>{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" style={{ padding: '60px 1.25rem', background: C.dark }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div className="fade-up">
            <span style={{ display: 'block', fontSize: '.71rem', fontWeight: 800, letterSpacing: '2.5px', textTransform: 'uppercase', color: C.yellow, marginBottom: 10 }}>How it works</span>
            <h2 style={{ fontSize: 'clamp(1.7rem, 4vw, 2.55rem)', marginBottom: 13, fontFamily: "'Fredoka One', cursive", color: '#fff' }}>Four steps, zero nagging</h2>
          </div>
          <style>{`
            .how-steps { display: grid; grid-template-columns: 1fr; gap: 22px; margin-top: 44px; }
            @media (min-width: 540px) { .how-steps { grid-template-columns: 1fr 1fr; gap: 18px; } }
            @media (min-width: 1000px) { .how-steps { grid-template-columns: repeat(4, 1fr); gap: 22px; } }
          `}</style>
          <div className="how-steps">
            {[
              { num: '01', title: 'Set habits together', desc: 'Pick 3 habits with your child. When they help choose, they actually follow through.', svg: (
                <svg viewBox="0 0 100 100" fill="none" style={{ width: 90, height: 90 }}>
                  <circle cx="36" cy="28" r="14" stroke="#FF6B35" strokeWidth="2.5" fill="rgba(255,107,53,.1)"/>
                  <path d="M16 72C16 56 24 48 36 48S56 56 56 72" stroke="#FF6B35" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
                  <circle cx="64" cy="28" r="14" stroke="#2EC4B6" strokeWidth="2.5" fill="rgba(46,196,182,.1)"/>
                  <path d="M44 72C44 56 52 48 64 48S84 56 84 72" stroke="#2EC4B6" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
                  <circle cx="50" cy="74" r="13" fill="rgba(255,209,102,.15)" stroke="#FFD166" strokeWidth="2"/>
                  <path d="M43 74L48 80 57 67" stroke="#FFD166" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
              )},
              { num: '02', title: 'Check in daily', desc: 'Kids tap to mark habits done. Streaks build momentum without any nagging.', svg: (
                <svg viewBox="0 0 100 100" fill="none" style={{ width: 90, height: 90 }}>
                  <circle cx="50" cy="50" r="36" stroke="rgba(255,255,255,.08)" strokeWidth="7" fill="none"/>
                  <circle cx="50" cy="50" r="36" stroke="#2EC4B6" strokeWidth="7" fill="none" strokeDasharray="198" strokeDashoffset="50" strokeLinecap="round" transform="rotate(-90 50 50)"/>
                  <path d="M50 68C42 68 36 62 36 54 36 46 42 42 46 38 46 44 50 46 50 46 50 46 56 40 54 34 60 38 64 46 64 54 64 62 58 68 50 68Z" fill="#FF6B35" opacity=".9"/>
                  <path d="M50 68C46 68 43 65 43 61 43 57 46 55 48 53 48 56 50 57 50 57 50 57 53 54 52 51 55 53 57 57 57 61 57 65 54 68 50 68Z" fill="#FFD166" opacity=".85"/>
                  <text x="50" y="88" fontFamily="Fredoka One,cursive" fontSize="11" fill="rgba(255,255,255,.5)" textAnchor="middle">Day 7</text>
                </svg>
              )},
              { num: '03', title: 'Earn real rewards', desc: 'Points become rewards your family actually values — set by you, not us.', svg: (
                <svg viewBox="0 0 100 100" fill="none" style={{ width: 90, height: 90 }}>
                  <path d="M50 20L55 35 71 35 58 44 63 60 50 51 37 60 42 44 29 35 45 35Z" fill="#FFD166" opacity=".9"/>
                  <path d="M22 72C22 57 34 45 50 45 66 45 78 57 78 72" stroke="#FF6B35" strokeWidth="2" strokeDasharray="5 4" strokeLinecap="round" fill="none" opacity=".5"/>
                  <circle cx="22" cy="72" r="5" fill="#FF6B35" opacity=".7"/>
                  <circle cx="78" cy="72" r="5" fill="#FF6B35" opacity=".7"/>
                  <circle cx="50" cy="45" r="4" fill="#FFD166" opacity=".6"/>
                  <text x="50" y="91" fontFamily="Fredoka One,cursive" fontSize="11" fill="rgba(255,255,255,.5)" textAnchor="middle">250 points</text>
                </svg>
              )},
              { num: '04', title: 'Celebrate out loud', desc: 'Badges, parent notes, and milestone moments kids actually remember.', svg: (
                <svg viewBox="0 0 100 100" fill="none" style={{ width: 90, height: 90 }}>
                  <path d="M38 44L30 72 50 64 70 72 62 44Z" fill="#FF6B35" opacity=".7"/>
                  <circle cx="50" cy="38" r="22" fill="#FFD166" stroke="#FF6B35" strokeWidth="2.5"/>
                  <circle cx="50" cy="38" r="16" fill="#FF6B35" opacity=".9"/>
                  <path d="M39 38L43 44 50 38 57 44 61 38" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  <path d="M12 18L13.5 13 15 18 20 19.5 15 21 13.5 26 12 21 7 19.5Z" fill="#FFD166" opacity=".55"/>
                  <path d="M82 22L83.5 18 85 22 89 23 85 24 83.5 28 82 24 78 23Z" fill="#2EC4B6" opacity=".55"/>
                  <rect x="16" y="52" width="7" height="7" rx="1.5" fill="#2EC4B6" opacity=".45" transform="rotate(30 19.5 55.5)"/>
                  <rect x="77" y="48" width="6" height="6" rx="1.5" fill="#FF6B35" opacity=".45" transform="rotate(-25 80 51)"/>
                  <text x="50" y="87" fontFamily="Fredoka One,cursive" fontSize="10.5" fill="rgba(255,255,255,.5)" textAnchor="middle">Rising Star!</text>
                </svg>
              )},
            ].map((s, i) => (
              <div key={s.num} className="fade-up" style={{ transitionDelay: `${0.04 * (i + 1)}s` }}>
                <div style={{ fontFamily: "'Fredoka One', cursive", fontSize: '2.8rem', color: 'rgba(255,209,102,.1)', lineHeight: 1, marginBottom: -2 }}>{s.num}</div>
                <div style={{
                  width: '100%', aspectRatio: '3/2',
                  background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.07)',
                  borderRadius: 18, marginBottom: 16,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
                }}>{s.svg}</div>
                <h3 style={{ fontFamily: "'Fredoka One', cursive", fontSize: '1.18rem', color: '#fff', marginBottom: 7 }}>{s.title}</h3>
                <p style={{ fontSize: '.86rem', color: 'rgba(255,255,255,.46)', lineHeight: 1.65, margin: 0 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARENT COMMITMENT */}
      <section style={{ padding: '60px 1.25rem', background: C.yellowL }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div className="fade-up">
            <span style={{ display: 'block', fontSize: '.71rem', fontWeight: 800, letterSpacing: '2.5px', textTransform: 'uppercase', color: C.orange, marginBottom: 10 }}>The parent promise</span>
            <h2 style={{ fontSize: 'clamp(1.7rem, 4vw, 2.55rem)', marginBottom: 13, fontFamily: "'Fredoka One', cursive" }}>This only works if you show up too</h2>
            <p style={{ fontSize: '1rem', color: C.muted, maxWidth: 520, lineHeight: 1.8 }}>SuperFit isn't a set-it-and-forget-it app. It's a tool for something you already want to do — be present.</p>
          </div>
          <style>{`
            .commit-grid { display: flex; flex-direction: column; gap: 32px; margin-top: 40px; }
            @media (min-width: 768px) { .commit-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 52px; } }
          `}</style>
          <div className="commit-grid">
            <ul style={{ listStyle: 'none' }}>
              {[
                { title: '5 minutes to set up', desc: 'Pick 3 habits, set a reward. Start small. Change them any time.' },
                { title: 'One note a week', desc: 'Leave a short encouragement note. Kids see it as the first thing on their home screen.' },
                { title: 'Celebrate small wins loudly', desc: 'A 7-day streak is massive for a 6-year-old. Treat it that way.' },
                { title: 'Let them fail sometimes', desc: "A broken streak isn't disaster. That lesson is worth more than the streak itself." },
              ].map((c, i) => (
                <li key={c.title} className="fade-up" style={{ display: 'flex', gap: 13, padding: '15px 0', borderBottom: i < 3 ? `1px solid rgba(26,26,46,.06)` : 'none', transitionDelay: `${0.05 * (i + 1)}s` }}>
                  <div style={{ width: 26, height: 26, background: C.orange, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                    <svg viewBox="0 0 12 12" fill="none" width="12" height="12"><path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, color: C.dark, marginBottom: 3, fontSize: '.95rem' }}>{c.title}</div>
                    <div style={{ fontSize: '.86rem', color: C.muted, lineHeight: 1.6 }}>{c.desc}</div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="fade-up" style={{ background: '#fff', borderRadius: 22, padding: '24px 24px 16px', boxShadow: '0 16px 48px rgba(26,26,46,.12)', transitionDelay: '.25s' }}>
              <h3 style={{ fontFamily: "'Fredoka One', cursive", fontSize: '1.22rem', color: C.dark, marginBottom: 16 }}>What SuperFit promises you</h3>
              {[
                "No ads, ever. Your kids' attention belongs to your family.",
                "No dark patterns or addictive loops designed to keep them on-screen longer.",
                "Rewards you control. No virtual currency, no loot boxes.",
                "Complete data privacy. No third-party sharing, ever.",
                "Built for Indian families. English + Hindi from day one. 6 more languages coming.",
              ].map((t, i) => (
                <div key={i} style={{ display: 'flex', gap: 11, alignItems: 'flex-start', padding: '9px 0', borderBottom: i < 4 ? `1px solid ${C.border}` : 'none' }}>
                  <div style={{ width: 8, height: 8, background: C.teal, borderRadius: '50%', flexShrink: 0, marginTop: 7 }} />
                  <p style={{ fontSize: '.88rem', color: C.text, lineHeight: 1.65, margin: 0 }}>{t}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="download" style={{ padding: '60px 1.25rem', textAlign: 'center' }}>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 26 }}>
            <svg viewBox="0 0 180 162" fill="none" style={{ width: 'min(180px, 55vw)', height: 'auto' }}>
              <circle cx="90" cy="82" r="66" fill="#FFF0EB" opacity=".6"/>
              <rect x="17" y="20" width="11" height="11" rx="2.5" fill="#FFD166" opacity=".62" transform="rotate(20 22.5 25.5)"/>
              <rect x="150" y="17" width="9" height="9" rx="2" fill="#2EC4B6" opacity=".62" transform="rotate(-25 154.5 21.5)"/>
              <rect x="11" y="114" width="10" height="10" rx="2" fill="#FF6B35" opacity=".52" transform="rotate(38 16 119)"/>
              <rect x="158" y="104" width="9" height="9" rx="2" fill="#6C63FF" opacity=".52" transform="rotate(-18 162.5 108.5)"/>
              <circle cx="44" cy="71" r="20" fill="#D4956A"/>
              <rect x="26" y="88" width="36" height="42" rx="12" fill="#2EC4B6"/>
              <circle cx="90" cy="65" r="24" fill="#D4956A"/>
              <rect x="70" y="84" width="40" height="50" rx="12" fill="#FF6B35"/>
              <path d="M90 91L93.5 100 103 100 96 105.5 99 115 90 110 81 115 84 105.5 77 100 86.5 100Z" fill="#FFD166"/>
              <circle cx="136" cy="71" r="20" fill="#D4956A"/>
              <rect x="118" y="88" width="36" height="42" rx="12" fill="#2EC4B6"/>
              <path d="M60 112L70 116" stroke="#D4956A" strokeWidth="12" strokeLinecap="round" fill="none"/>
              <path d="M110 116L120 112" stroke="#D4956A" strokeWidth="12" strokeLinecap="round" fill="none"/>
              <path d="M90 34L92.5 26 95 34 103 36 95 38 92.5 46 90 38 82 36Z" fill="#FFD166"/>
              <path d="M44 48L45.5 44 47 48 51 49 47 50 45.5 54 44 50 40 49Z" fill="#FF6B35" opacity=".5"/>
              <path d="M136 48L137.5 44 139 48 143 49 139 50 137.5 54 136 50 132 49Z" fill="#FF6B35" opacity=".5"/>
            </svg>
          </div>
          <h2 className="fade-up" style={{ fontSize: 'clamp(1.8rem, 5vw, 2.8rem)', marginBottom: 12, fontFamily: "'Fredoka One', cursive" }}>Ready to try a different morning?</h2>
          <p className="fade-up" style={{ fontSize: '1.03rem', color: C.muted, marginBottom: 28 }}>Free to download. No credit card. No tricks.</p>
          <style>{`
            .store-btns { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; margin-bottom: 12px; }
            @media (max-width: 380px) { .store-btns { flex-direction: column; align-items: center; } }
          `}</style>
          <div className="store-btns fade-up">
            <a href="https://apps.apple.com/app/superfit-family" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: C.dark, color: '#fff', padding: '12px 18px', borderRadius: 13, transition: 'transform .17s, opacity .17s' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="white"><path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zm3.261-2.842c.84-1.021 1.403-2.442 1.247-3.87-1.208.052-2.668.806-3.535 1.827-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.561-1.671z"/></svg>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '.65rem', opacity: .5 }}>Download on the</span>
                <span style={{ fontFamily: "'Fredoka One', cursive", fontSize: '.96rem' }}>App Store</span>
              </div>
            </a>
            <a href="https://play.google.com/store/apps/details?id=in.superfitfamily.app" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: C.dark, color: '#fff', padding: '12px 18px', borderRadius: 13 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M3.18 23.71L13.88 13 3.18.29C2.47.6 2 1.33 2 2.18v19.64c0 .85.47 1.58 1.18 1.89z" fill="white"/>
                <path d="M20.64 10.07l-2.46-1.38L15.15 13l2.99 2.99 2.55-1.43c.79-.44.79-1.57 0-2.01" fill="white"/>
                <path d="M13.88 13L3.18 23.71l.64.36 12.07-6.77L13.88 13z" fill="white" opacity=".85"/>
                <path d="M3.82.24L13.88 13l2.01-2.01L5.89.12 3.82.24z" fill="white" opacity=".85"/>
              </svg>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '.65rem', opacity: .5 }}>Get it on</span>
                <span style={{ fontFamily: "'Fredoka One', cursive", fontSize: '.96rem' }}>Google Play</span>
              </div>
            </a>
            <a href={APP_URL} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: 'rgba(26,26,46,.7)', border: '1.5px solid rgba(255,255,255,.13)', color: '#fff', padding: '12px 18px', borderRadius: 13 }}>
              <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '.65rem', opacity: .5 }}>Open in browser</span>
                <span style={{ fontFamily: "'Fredoka One', cursive", fontSize: '.96rem' }}>Web App</span>
              </div>
            </a>
          </div>
          <p style={{ fontSize: '.79rem', color: '#B2BEC3' }}>Free for families · iOS, Android &amp; Web</p>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ background: C.dark, padding: '30px 1.25rem', textAlign: 'center' }}>
        <div style={{ display: 'flex', gap: 18, justifyContent: 'center', marginBottom: 11, flexWrap: 'wrap' }}>
          <a href="/privacy" style={{ color: 'rgba(255,255,255,.36)', fontSize: '.83rem', transition: 'color .2s' }}>Privacy Policy</a>
          <a href="https://superfit-dashboard-app.vercel.app" style={{ color: 'rgba(255,255,255,.36)', fontSize: '.83rem' }}>Parent Dashboard</a>
          <a href="mailto:hello@superfitfamily.in" style={{ color: 'rgba(255,255,255,.36)', fontSize: '.83rem' }}>Contact</a>
        </div>
        <p style={{ fontSize: '.77rem', color: 'rgba(255,255,255,.18)', margin: 0 }}>© 2025 SuperFit Family. Made with ♥ for Indian families.</p>
      </footer>
    </div>
  );
};

// ── APP ────────────────────────────────────────────────
export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/privacy.html" element={<PrivacyPage />} />
        <Route path="*" element={<LandingPage />} />
      </Routes>
      <Analytics />
    </BrowserRouter>
  );
}
